/**
 * Module-scoped registry that hands transports OUTSIDE Apollo a fresh access
 * token at the moment they dial (SE-8687).
 *
 * The Ask Gemini SSE streams (chat, AI summaries, dictation) are plain
 * `EventSource` POSTs, so they never pass through `authLink` — the only place
 * that refreshed an expiring token. They used to read `auth.data.accessToken`
 * from render, which is whatever the last GraphQL request left behind: a user
 * who spent an hour on Ask Gemini without triggering a query sent an expired
 * token, the agent answered 401, and every question failed with "Connection
 * error".
 *
 * {@link ApolloClientProvider} registers a reader that runs the same
 * proactive refresh `authLink` runs (joining an in-flight refresh rather than
 * starting a second one) and then returns the current token. The provider
 * can't be a context dependency of these hooks without threading it through
 * every caller, so — like `povTeamHeaderState` — it pushes into this module
 * instead.
 * @module providers/accessTokenSource
 */

/** Resolves the current access token, refreshing it first when it is expiring. */
export type AccessTokenReader = () => Promise<string | undefined>;

/** The installed reader; a ref-shaped holder so the binding itself stays const. */
const registry: { current: AccessTokenReader | null } = { current: null };

/**
 * Registers the reader {@link getFreshAccessToken} delegates to.
 * @param next - The reader to install
 * @returns A disposer that removes `next`, and only `next`, so an unmount
 * racing a remount cannot uninstall its successor
 */
export const registerAccessTokenReader = (
  next: AccessTokenReader
): (() => void) => {
  registry.current = next;
  return () => {
    if (registry.current === next) {
      registry.current = null;
    }
  };
};

/**
 * Reads the access token at request time, refreshing it first if it is
 * expired or about to expire. Call it immediately before building a request —
 * never cache the result across requests.
 * @param fallback - Token to use when no reader is registered (no
 * `ApolloClientProvider` mounted, e.g. an isolated test)
 * @returns The fresh access token, or undefined when signed out
 */
export const getFreshAccessToken = async (
  fallback?: string | null
): Promise<string | undefined> => {
  const read = registry.current;
  if (!read) {
    return fallback || undefined;
  }
  return (await read()) || undefined;
};
